import React, { useEffect, useMemo, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
} from "../../../components/BaseUI/Dropdown";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { FiPackage, FiSave, FiChevronLeft, FiTrendingUp } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import "./AddInventory.scss";
import { dateStrInAppTz } from "../../../utils/appTime";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const todayStr = () => dateStrInAppTz(new Date());

const isStockEntryAllowedSource = (v) =>
  v === undefined || v === null || v === "purchased" || v === "mixed";

const partSource = (inv) =>
  inv?.partId && typeof inv.partId === "object"
    ? inv.partId.source
    : undefined;

/** Stok girişi: satın alınan / karma veya kaynak belirsiz (item veya part) */
const isStockEntryEligibleItem = (inv) =>
  isStockEntryAllowedSource(inv?.source) ||
  isStockEntryAllowedSource(partSource(inv));

const AddInventory = () => {
  useTitle("Rovion MES - Envanter Ekle");
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/inventory",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [items, setItems] = useState([]);
  const [itemSearch, setItemSearch] = useState("");
  const [formData, setFormData] = useState({
    inventoryItemId: "",
    quantityAdded: "",
    purchasedDate: todayStr(),
    itemNote: "",
    notes: "",
  });

  const preselectItemId = useMemo(() => {
    const fromParams = params?.id || params?.itemId;
    const fromQuery = searchParams?.get("itemId") || searchParams?.get("id");
    const fromState =
      location?.state?.itemId ||
      location?.state?.inventoryItemId ||
      location?.state?.id;
    return fromParams || fromQuery || fromState || "";
  }, [location?.state, params?.id, params?.itemId, searchParams]);

  const addToast = (message, type = "info") => {
    const newToast = { id: Date.now(), message, type };
    setToasts((prev) => [...prev, newToast]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== newToast.id)),
      3000,
    );
  };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/inventory`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const data = res.data?.items || res.data;
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
  };

  const stockEntryItems = useMemo(
    () => (items || []).filter(isStockEntryEligibleItem),
    [items],
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchItems()]);
      setLoading(false);
    };
    load();
  }, []);

  useEffect(() => {
    if (!preselectItemId) return;
    if (!stockEntryItems?.length) return;
    const exists = stockEntryItems.some(
      (i) => String(i?._id) === String(preselectItemId),
    );
    if (!exists) return;
    setFormData((prev) => ({
      ...prev,
      inventoryItemId: preselectItemId,
      quantityAdded:
        prev.quantityAdded !== "" && Number(prev.quantityAdded) > 0
          ? prev.quantityAdded
          : "1",
      purchasedDate: prev.purchasedDate || todayStr(),
    }));
  }, [stockEntryItems, preselectItemId]);

  useEffect(() => {
    if (!formData.inventoryItemId) return;
    const ok = stockEntryItems.some(
      (i) => String(i._id) === String(formData.inventoryItemId),
    );
    if (!ok) setFormData((p) => ({ ...p, inventoryItemId: "" }));
  }, [stockEntryItems, formData.inventoryItemId]);

  const handleSubmit = async () => {
    if (!canWrite) return;
    if (!formData.inventoryItemId) {
      addToast("Lütfen bir envanter kalemi seçin", "warning");
      return;
    }
    const qty = Number(formData.quantityAdded);
    if (!qty || qty <= 0) {
      addToast("Miktar 0'dan büyük olmalıdır", "warning");
      return;
    }
    const payloadItems = [
      {
        inventoryItemId: formData.inventoryItemId,
        quantityAdded: qty,
        purchasedDate: formData.purchasedDate || todayStr(),
        notes: formData.itemNote || "",
      },
    ];
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/inventory/additions`,
        { items: payloadItems, notes: formData.notes || "" },
        { headers: { Authorization: `Bearer ${getToken()}` } },
      );
      addToast("Envanter eklendi", "success");
      navigate("/inventory");
    } catch (err) {
      addToast(err.response?.data?.message || "Eklenemedi", "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: "Rovion", url: "" },
    { label: "Envanter", url: "/inventory" },
    { label: "Envanter Ekle" },
  ];

  const filteredItems = useMemo(() => {
    if (!itemSearch?.trim()) return stockEntryItems;
    const q = itemSearch.toLowerCase();
    return stockEntryItems.filter(
      (i) =>
        i?.partId?.name?.toLowerCase().includes(q) ||
        i?.partId?.code?.toLowerCase().includes(q) ||
        i?.name?.toLowerCase().includes(q) ||
        i?.code?.toLowerCase().includes(q),
    );
  }, [stockEntryItems, itemSearch]);
  const selectedItem = useMemo(
    () => filteredItems.find((i) => i?._id === formData.inventoryItemId),
    [filteredItems, formData.inventoryItemId],
  );

  if (loading) {
    return (
      <AppPage title="Envanter Ekle" breadcrumbs={breadcrumbs}>
        <CardContainer>
          <div className="loading-container">
            <FiPackage className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  return (
    <AppPage title="Envanter Ekle" breadcrumbs={breadcrumbs}>
      <div className="add-inventory-page">
        <CardContainer className="add-inventory__items-card">
          <div className="add-inventory__form">
            <div className="add-inventory__field add-inventory__field--wide">
              <Label required>Kalem Seç</Label>
              <div
                className="form-control-container border-1 border-gray-300"
                style={{ border: "1px solid #e2e8f0", borderRadius: "5px" }}
              >
                <Dropdown
                  width="auto"
                  maxHeight="360px"
                  closeOnSelect
                  trigger={
                    <div className="d-flex align-items-center justify-content-between p-2">
                      <span className="dropdown-text">
                        {selectedItem
                          ? `${selectedItem.partId?.name || selectedItem.name || "—"} (${selectedItem.partId?.code || selectedItem.code || "—"})`
                          : "Kalem seçin"}
                      </span>
                    </div>
                  }
                >
                  <DropdownContent className="p-2">
                    <div className="mb-2">
                      <Input
                        type="text"
                        placeholder="Ara (ad veya kod)..."
                        value={itemSearch}
                        onChange={(v) => setItemSearch(v)}
                      />
                    </div>
                    {filteredItems.length > 0 ? (
                      filteredItems.map((item) => (
                        <DropdownItem
                          key={item._id}
                          active={item._id === formData.inventoryItemId}
                          onClick={() =>
                            setFormData((p) => ({
                              ...p,
                              inventoryItemId: item._id,
                            }))
                          }
                        >
                          <div className="d-flex align-items-center justify-content-between gap-2">
                            <div className="d-flex flex-column">
                              <span style={{ fontWeight: 600 }}>
                                {item.partId?.name || item.name || "—"}
                              </span>
                              <small className="text-muted">
                                {item.partId?.code || item.code || "—"}
                              </small>
                            </div>
                            <small className="text-muted">
                              {item.quantity ?? 0}
                            </small>
                          </div>
                        </DropdownItem>
                      ))
                    ) : (
                      <DropdownItem disabled>
                        {stockEntryItems.length === 0
                          ? "Stok girişi yapılabilecek envanter kalemi yok"
                          : "Sonuç bulunamadı"}
                      </DropdownItem>
                    )}
                  </DropdownContent>
                </Dropdown>
              </div>
            </div>

            <div className="add-inventory__field">
              <Label required>Satın Alma Tarihi</Label>
              <Input
                type="date"
                value={formData.purchasedDate}
                onChange={(v) =>
                  setFormData((p) => ({
                    ...p,
                    purchasedDate: v || todayStr(),
                  }))
                }
              />
            </div>

            <div className="add-inventory__field">
              <Label required>Miktar</Label>
              <Input
                type="number"
                min="1"
                step="1"
                placeholder="1"
                value={formData.quantityAdded}
                onChange={(v) => {
                  const num = (v || "").toString().replace(/[^0-9]/g, "");
                  setFormData((p) => ({ ...p, quantityAdded: num }));
                }}
              />
            </div>
            <div className="add-inventory__field add-inventory__field--wide">
              <Label>Not</Label>
              <Input
                type="text"
                placeholder="Bu stok girişi için not..."
                value={formData.notes}
                onChange={(v) => setFormData((p) => ({ ...p, notes: v ?? "" }))}
              />
            </div>
          </div>

          <div className="add-inventory__footer">
            <div className="add-inventory__actions">
              <Button
                appearance="danger-subtle"
                onClick={() => navigate("/inventory")}
              >
                İptal
              </Button>
              <Button
                appearance="primary-animated"
                icon={<FiSave />}
                onClick={handleSubmit}
                disabled={submitting || !canWrite || !formData.inventoryItemId}
              >
                {submitting ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </div>
          </div>
        </CardContainer>
      </div>
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type}
            onClose={() => setToasts((p) => p.filter((x) => x.id !== t.id))}
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default withAuth(AddInventory, ["admin", "employee", "user"]);
